// ================================================================
// src/components/books/BooksSharedUI.jsx
// ================================================================
// کامپوننت‌های مشترک UI برای صفحه‌های معلم و مدیر
// کاملاً روی hooks فایل ۴ سوار می‌شه
// ----------------------------------------------------------------
// بخش‌ها:
//   ۱. Primitive‌ها: Spinner, ProgressBar, Badge, Chip
//   ۲. Layout: EmptyState, SkeletonLoader, LockBanner, AutoSaveIndicator
//   ۳. Modal + ConfirmModal + Wizard
//   ۴. Media: BlobImage, ImageUploader, VideoUploader, VideoPreview
//   ۵. DiffViewer
//   ۶. ImpactPanel
//   ۷. VersionBadge
//   ۸. NotificationBell
// ================================================================

import React, {
  useState, useEffect, useRef, useCallback, useMemo, useId,
} from 'react';
import { createPortal } from 'react-dom';
import {
  useBlobUrl, useVideoUpload, useImageUpload,
  useNotifications, useUnreadCount,
} from '../../hooks/useBooksEngine.js';

import CircularProgress from '../../core/books/CircularProgress';
// ================================================================
// SECTION 1: CONSTANTS & ICONS
// ================================================================

const FOCUSABLE_SELECTOR = [
  'a[href]', 'button:not([disabled])', 'input:not([disabled])',
  'select:not([disabled])', 'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

const MODAL_Z = 9000;
const MODAL_Z_NESTED = 9500;
const NOTIF_Z = 8500;

const VideoIcon = (p) => (
  <svg viewBox="0 0 24 24" width={p.size || 20} height={p.size || 20} fill="none" {...p}>
    <path d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14v-4z" fill="currentColor" opacity="0.35"/>
    <rect x="3" y="6" width="12" height="12" rx="2" stroke="currentColor" strokeWidth="1.7"/>
  </svg>
);

const CloseIcon = (p) => (
  <svg viewBox="0 0 24 24" width={p.size || 18} height={p.size || 18} fill="none" {...p}>
    <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

const CheckIcon = (p) => (
  <svg viewBox="0 0 24 24" width={p.size || 18} height={p.size || 18} fill="none" {...p}>
    <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const AlertIcon = (p) => (
  <svg viewBox="0 0 24 24" width={p.size || 20} height={p.size || 20} fill="none" {...p}>
    <path d="M12 9v4M12 17h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const LockIcon = (p) => (
  <svg viewBox="0 0 24 24" width={p.size || 18} height={p.size || 18} fill="none" {...p}>
    <rect x="4" y="11" width="16" height="10" rx="2" stroke="currentColor" strokeWidth="1.7"/>
    <path d="M8 11V7a4 4 0 118 0v4" stroke="currentColor" strokeWidth="1.7"/>
  </svg>
);

const BellIcon = (p) => (
  <svg viewBox="0 0 24 24" width={p.size || 20} height={p.size || 20} fill="none" {...p}>
    <path d="M6 8a6 6 0 1112 0c0 7 3 8 3 8H3s3-1 3-8zM10.3 21a1.94 1.94 0 003.4 0" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ChevronIcon = ({ dir = 'down', size = 16 }) => {
  const paths = {
    down: 'M6 9l6 6 6-6',
    up: 'M6 15l6-6 6 6',
    left: 'M15 6l-6 6 6 6',
    right: 'M9 6l6 6-6 6',
  };
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none">
      <path d={paths[dir]} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
};

const UploadIcon = (p) => (
  <svg viewBox="0 0 24 24" width={p.size || 32} height={p.size || 32} fill="none" {...p}>
    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const TrashIcon = (p) => (
  <svg viewBox="0 0 24 24" width={p.size || 16} height={p.size || 16} fill="none" {...p}>
    <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6M10 11v6M14 11v6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const EditIcon = (p) => (
  <svg viewBox="0 0 24 24" width={p.size || 16} height={p.size || 16} fill="none" {...p}>
    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const PlusIcon = (p) => (
  <svg viewBox="0 0 24 24" width={p.size || 16} height={p.size || 16} fill="none" {...p}>
    <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

const PlayIcon = (p) => (
  <svg viewBox="0 0 24 24" width={p.size || 22} height={p.size || 22} fill="none" {...p}>
    <path d="M8 5v14l11-7L8 5z" fill="currentColor"/>
  </svg>
);

const SaveIcon = (p) => (
  <svg viewBox="0 0 24 24" width={p.size || 16} height={p.size || 16} fill="none" {...p}>
    <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z M17 21v-8H7v8M7 3v5h8" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// ================================================================
// SECTION 2: PRIMITIVES
// ================================================================

export function Spinner({ size = 18, color = 'currentColor', className = '' }) {
  return (
    <span
      className={`bks-spinner ${className}`}
      style={{
        width: size, height: size,
        borderWidth: Math.max(2, Math.floor(size / 9)),
        borderColor: `${color} transparent ${color} ${color}`,
      }}
      role="status"
      aria-label="در حال بارگذاری"
    />
  );
}

export function ProgressBar({ value = 0, max = 100, height = 8, showLabel = false, className = '' }) {
  const pct = Math.max(0, Math.min(100, (value / max) * 100));
  return (
    <div className={`bks-progress-wrap ${className}`}>
      <div className="bks-progress" style={{ height }}>
        <div className="bks-progress-fill" style={{ width: `${pct}%` }} />
      </div>
      {showLabel && <span className="bks-progress-label">{Math.round(pct)}%</span>}
    </div>
  );
}

const BADGE_TONES = {
  neutral: { bg: '#f1f5f9', fg: '#475569', bd: '#cbd5e1' },
  info:    { bg: '#dbeafe', fg: '#1e40af', bd: '#93c5fd' },
  success: { bg: '#dcfce7', fg: '#166534', bd: '#86efac' },
  warning: { bg: '#fef3c7', fg: '#92400e', bd: '#fcd34d' },
  danger:  { bg: '#fee2e2', fg: '#991b1b', bd: '#fca5a5' },
  purple:  { bg: '#ede9fe', fg: '#5b21b6', bd: '#c4b5fd' },
};

export function Badge({ tone = 'neutral', icon = null, children, size = 'md', className = '' }) {
  const t = BADGE_TONES[tone] || BADGE_TONES.neutral;
  const pad = size === 'sm' ? '2px 8px' : '4px 12px';
  const fs = size === 'sm' ? '0.7rem' : '0.78rem';
  return (
    <span
      className={`bks-badge ${className}`}
      style={{
        background: t.bg, color: t.fg, border: `1px solid ${t.bd}`,
        padding: pad, fontSize: fs,
      }}
    >
      {icon && <span className="bks-badge-icon">{icon}</span>}
      {children}
    </span>
  );
}

export function Chip({ children, onRemove, tone = 'neutral', className = '' }) {
  const t = BADGE_TONES[tone] || BADGE_TONES.neutral;
  return (
    <span
      className={`bks-chip ${className}`}
      style={{ background: t.bg, color: t.fg, border: `1px solid ${t.bd}` }}
    >
      {children}
      {onRemove && (
        <button
          type="button"
          className="bks-chip-x"
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
          aria-label="حذف"
        >
          <CloseIcon size={12} />
        </button>
      )}
    </span>
  );
}

// ================================================================
// SECTION 3: LAYOUT COMPONENTS
// ================================================================

export function EmptyState({
  icon = '📭',
  title = 'هیچ موردی یافت نشد',
  description = '',
  action = null,
  size = 'md',
  className = '',
}) {
  return (
    <div className={`bks-empty bks-empty-${size} ${className}`} role="status">
      <div className="bks-empty-icon">{typeof icon === 'string' ? icon : icon}</div>
      <div className="bks-empty-title">{title}</div>
      {description && <div className="bks-empty-desc">{description}</div>}
      {action && <div className="bks-empty-action">{action}</div>}
    </div>
  );
}

export function SkeletonLoader({ variant = 'card', count = 1, className = '' }) {
  const items = Array.from({ length: count });
  return (
    <div className={`bks-skeleton bks-skeleton-${variant} ${className}`} aria-hidden="true">
      {items.map((_, i) => {
        if (variant === 'text') return <div key={i} className="bks-sk-text" />;
        if (variant === 'line') return <div key={i} className="bks-sk-line" />;
        if (variant === 'avatar') return <div key={i} className="bks-sk-avatar" />;
        return (
          <div key={i} className="bks-sk-card">
            <div className="bks-sk-thumb" />
            <div className="bks-sk-line" />
            <div className="bks-sk-line short" />
          </div>
        );
      })}
    </div>
  );
}

export function LockBanner({ reason = 'این بخش در حال حاضر قابل ویرایش نیست.', icon = '🔒' }) {
  return (
    <div className="bks-lock-banner" role="alert">
      <span className="bks-lock-icon">{icon}</span>
      <span className="bks-lock-text">{reason}</span>
    </div>
  );
}

export function AutoSaveIndicator({
  isSaving = false,
  hasUnsaved = false,
  lastSavedAt = null,
  onForceSave = null,
  className = '',
}) {
  const status = isSaving ? 'saving' : hasUnsaved ? 'unsaved' : lastSavedAt ? 'saved' : 'idle';
  const labels = {
    idle: '',
    saving: 'در حال ذخیره...',
    unsaved: 'تغییرات ذخیره‌نشده',
    saved: 'ذخیره شد',
  };
  const tones = {
    idle: '#94a3b8',
    saving: '#3b82f6',
    unsaved: '#f59e0b',
    saved: '#10b981',
  };

  const timeAgo = useMemo(() => {
    if (!lastSavedAt) return '';
    const diff = Date.now() - lastSavedAt;
    if (diff < 5000) return 'همین حالا';
    if (diff < 60000) return `${Math.floor(diff / 1000)} ثانیه پیش`;
    if (diff < 3600000) return `${Math.floor(diff / 60000)} دقیقه پیش`;
    return `${Math.floor(diff / 3600000)} ساعت پیش`;
  }, [lastSavedAt, status]);

  if (status === 'idle') return null;

  return (
    <div className={`bks-autosave ${className}`} style={{ color: tones[status] }}>
      {status === 'saving' && <Spinner size={12} color={tones[status]} />}
      {status === 'unsaved' && <span className="bks-autosave-dot" />}
      {status === 'saved' && <CheckIcon size={12} />}
      <span className="bks-autosave-label">{labels[status]}</span>
      {timeAgo && <span className="bks-autosave-time">{timeAgo}</span>}
      {onForceSave && hasUnsaved && !isSaving && (
        <button
          type="button"
          className="bks-autosave-btn"
          onClick={onForceSave}
          title="ذخیره فوری"
        >
          <SaveIcon size={12} />
        </button>
      )}
    </div>
  );
}

// ================================================================
// SECTION 4: MODAL BASE
// ================================================================

// ✅ Portal مستقیماً به document.body — مرجع پایدار، هرگز stale نمی‌شود
function usePortalTarget() {
  return typeof document !== 'undefined' ? document.body : null;
}

export function Modal({
  isOpen,
  onClose,
  title = '',
  size = 'md', // sm | md | lg | xl | full
  children,
  footer = null,
  closeOnBackdrop = true,
  closeOnEsc = true,
  showCloseButton = true,
  initialFocusSelector = null,
  className = '',
  'aria-label': ariaLabel,
}) {
  const portal = usePortalTarget();
  const containerRef = useRef(null);
  const previousFocusRef = useRef(null);
  const titleId = useId();

  // focus trap + esc + scroll lock
  useEffect(() => {
    if (!isOpen) return;

    previousFocusRef.current = document.activeElement;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const container = containerRef.current;
    const focusable = container
      ? Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter((el) => el.offsetParent !== null)
      : [];

    // initial focus
    let toFocus = null;
    if (initialFocusSelector && container) {
      toFocus = container.querySelector(initialFocusSelector);
    }
    if (!toFocus) toFocus = focusable[0];
    if (toFocus) {
      setTimeout(() => { try { toFocus.focus(); } catch {} }, 30);
    }

    const onKeyDown = (e) => {
      if (e.key === 'Escape' && closeOnEsc) {
        e.stopPropagation();
        onClose?.();
        return;
      }
      if (e.key !== 'Tab') return;
      const current = document.activeElement;
      const list = container
        ? Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter((el) => el.offsetParent !== null)
        : [];
      if (list.length === 0) return;
      const idx = list.indexOf(current);
      if (e.shiftKey) {
        if (idx <= 0) { e.preventDefault(); list[list.length - 1].focus(); }
      } else {
        if (idx === list.length - 1) { e.preventDefault(); list[0].focus(); }
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
      try { previousFocusRef.current?.focus?.(); } catch {}
    };
  }, [isOpen, closeOnEsc, onClose, initialFocusSelector]);

  if (!isOpen || !portal) return null;

  const sizeClass = `bks-modal-${size}`;

  const handleBackdrop = (e) => {
    if (e.target === e.currentTarget && closeOnBackdrop) onClose?.();
  };

  return createPortal(
    <div
      className={`bks-modal-backdrop ${className}`}
      style={{ zIndex: MODAL_Z }}
      onMouseDown={handleBackdrop}
      role="presentation"
    >
      <div
        ref={containerRef}
        className={`bks-modal ${sizeClass}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : (ariaLabel ? undefined : titleId)}
        aria-label={ariaLabel}
      >
        {(title || showCloseButton) && (
          <header className="bks-modal-header">
            <h2 id={titleId} className="bks-modal-title">{title}</h2>
            {showCloseButton && (
              <button
                type="button"
                className="bks-modal-close"
                onClick={onClose}
                aria-label="بستن"
              >
                <CloseIcon />
              </button>
            )}
          </header>
        )}
        <div className="bks-modal-body">{children}</div>
        {footer && <footer className="bks-modal-footer">{footer}</footer>}
      </div>
    </div>,
    portal
  );
}

// ================================================================
// SECTION 5: CONFIRM MODAL
// ================================================================

export function ConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  title = 'تأیید عملیات',
  message = 'آیا از انجام این کار مطمئن هستید؟',
  confirmText = 'تأیید',
  cancelText = 'انصراف',
  tone = 'danger', // danger | warning | info
  requireReason = false,
  reasonLabel = 'دلیل (اختیاری)',
  reasonPlaceholder = '',
  reasonRequired = false,
  loading = false,
  extraContent = null,
}) {
  const [reason, setReason] = useState('');
  const [ack, setAck] = useState(false);
  const reasonRef = useRef(null);

  useEffect(() => {
    if (isOpen) { setReason(''); setAck(false); }
  }, [isOpen]);

  const handleConfirm = useCallback(() => {
    if (requireReason && reasonRequired && !reason.trim()) {
      reasonRef.current?.focus();
      return;
    }
    if (tone === 'danger' && !requireReason && !ack) return;
    onConfirm?.(reason.trim() || null);
  }, [reason, requireReason, reasonRequired, onConfirm, tone, ack]);

  const toneColor = {
    danger: '#ef4444',
    warning: '#f59e0b',
    info: '#3b82f6',
  }[tone] || '#ef4444';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="sm"
      closeOnBackdrop={!loading}
      closeOnEsc={!loading}
      initialFocusSelector={reasonRequired ? 'textarea' : '.bks-btn-primary'}
      footer={
        <>
          <button
            type="button"
            className="bks-btn bks-btn-ghost"
            onClick={onClose}
            disabled={loading}
          >
            {cancelText}
          </button>
          <button
            type="button"
            className="bks-btn bks-btn-primary"
            style={{ background: toneColor }}
            onClick={handleConfirm}
            disabled={
              loading ||
              (requireReason && reasonRequired && !reason.trim()) ||
              (tone === 'danger' && !requireReason && !ack)
            }
          >
            {loading ? <Spinner size={14} /> : null}
            {confirmText}
          </button>
        </>
      }
    >
      <div className="bks-confirm-body">
        <div className="bks-confirm-icon" style={{ color: toneColor }}>
          <AlertIcon size={28} />
        </div>
        <div className="bks-confirm-message">{message}</div>
        {extraContent}
        {requireReason && (
          <div className="bks-field" style={{ marginTop: 12 }}>
            <label className="bks-label">
              {reasonLabel}
              {reasonRequired && <span className="bks-req">*</span>}
            </label>
            <textarea
              ref={reasonRef}
              className="bks-textarea"
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={reasonPlaceholder}
            />
          </div>
        )}
        {tone === 'danger' && !requireReason && (
          <label className="bks-check" style={{ marginTop: 10 }}>
            <input
              type="checkbox"
              checked={ack}
              onChange={(e) => setAck(e.target.checked)}
            />
            <span>از انجام این کار مطمئن هستم</span>
          </label>
        )}
      </div>
    </Modal>
  );
}

// ================================================================
// SECTION 6: WIZARD
// ================================================================

export function Wizard({
  steps = [], // [{ key, label, icon?, validate?: () => boolean | Promise<boolean> }]
  currentStep = 0,
  onStepChange,
  onFinish,
  onCancel,
  loading = false,
  finishText = 'ثبت نهایی',
  cancelText = 'انصراف',
  error: externalError = null,   // ✅ جدید
  children, // یک function که stepKey می‌گیره و محتوا رندر می‌کنه
  className = '',
}) {
  const [internalError, setInternalError] = useState(null);
  const error = externalError || internalError;   // ✅ ترکیب
  const total = steps.length;
  const step = steps[currentStep];

    const goTo = useCallback(async (idx, direction) => {
    setInternalError(null);
    if (direction === 'next' && step?.validate) {
      try {
        const ok = await step.validate();
        if (!ok) {
          setInternalError('لطفاً اطلاعات این مرحله را کامل کنید.');
          return;
        }
      } catch (e) {
        setInternalError(e.message || 'اعتبارسنجی ناموفق بود');
        return;
      }
    }
    onStepChange?.(idx);
  }, [step, onStepChange]);

  const handleNext = useCallback(() => {
    if (currentStep < total - 1) goTo(currentStep + 1, 'next');
    else onFinish?.();
  }, [currentStep, total, goTo, onFinish]);

  const handleBack = useCallback(() => {
    if (currentStep > 0) goTo(currentStep - 1, 'back');
  }, [currentStep, goTo]);

  return (
    <div className={`bks-wizard ${className}`}>
      <div className="bks-wizard-steps" role="tablist" aria-label="مراحل">
        {steps.map((s, i) => {
          const state = i === currentStep ? 'active' : i < currentStep ? 'done' : 'pending';
          return (
            <div
              key={s.key}
              className={`bks-wizard-step bks-wizard-step-${state}`}
              role="tab"
              aria-selected={state === 'active'}
            >
              <div className="bks-wizard-dot">
                {state === 'done' ? <CheckIcon size={12} /> : (s.icon || (i + 1))}
              </div>
              <div className="bks-wizard-label">{s.label}</div>
              {i < total - 1 && <div className="bks-wizard-connector" />}
            </div>
          );
        })}
      </div>

      <div className="bks-wizard-body">
        {typeof children === 'function' ? children(step.key) : children}
      </div>

      {error && (
        <div className="bks-wizard-error" role="alert">
          <AlertIcon size={14} /> {error}
        </div>
      )}

      <div className="bks-wizard-footer">
        <button
          type="button"
          className="bks-btn bks-btn-ghost"
          onClick={currentStep === 0 ? onCancel : handleBack}
          disabled={loading}
        >
          {currentStep === 0 ? cancelText : 'مرحله قبل'}
        </button>
        <div className="bks-wizard-progress">
          {currentStep + 1} / {total}
        </div>
        <button
          type="button"
          className="bks-btn bks-btn-primary"
          onClick={handleNext}
          disabled={loading}
        >
          {loading ? <Spinner size={14} /> : null}
          {currentStep === total - 1 ? finishText : 'مرحله بعد'}
        </button>
      </div>
    </div>
  );
}

// ================================================================
// SECTION 7: MEDIA COMPONENTS
// ================================================================

export function BlobImage({
  blobId,
  fallbackSrc = null,
  alt = '',
  className = '',
  style = {},
  onClick = null,
}) {
  const { url, loading } = useBlobUrl(blobId);
  const src = url || fallbackSrc;

  if (loading && !src) {
    return <div className={`bks-blob-img bks-blob-loading ${className}`} style={style} />;
  }
  if (!src) {
    return (
      <div className={`bks-blob-img bks-blob-placeholder ${className}`} style={style}>
        <VideoIcon size={28} />
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      className={`bks-blob-img ${className}`}
      style={style}
      onClick={onClick}
      loading="lazy"
    />
  );
}

export function ImageUploader({
  value = null,
  onChange,
  label = 'تصویر',
  aspect = '16/9',
  maxSizeMB = 5,
  disabled = false,
  hint = '',
  refType = 'image',
}) {
  const { upload, uploading, error } = useImageUpload();
  const inputRef = useRef(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0); // 🆕
  const abortControllerRef = useRef(null); // 🆕

  const { url: existingUrl } = useBlobUrl(value);
  const displayUrl = previewUrl || existingUrl;

  useEffect(() => {
    return () => { if (previewUrl) URL.revokeObjectURL(previewUrl); };
  }, [previewUrl]);

  const doUpload = useCallback(async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) return;
    if (file.size > maxSizeMB * 1024 * 1024) return;
    const local = URL.createObjectURL(file);
    setPreviewUrl(local);

    const controller = new AbortController(); // 🆕
    abortControllerRef.current = controller; // 🆕
    setUploadProgress(0); // 🆕

    try {
      const res = await upload(file, {
        refType,
        onProgress: (p) => setUploadProgress(
          typeof p === 'object' ? (p?.percent ?? 0) : (p ?? 0)
        ),
        signal: controller.signal,
      });
      onChange?.(res.blobId);
    } catch (e) {
      setPreviewUrl(null);
    } finally {
      abortControllerRef.current = null; // 🆕
      setUploadProgress(0); // 🆕
    }
  }, [upload, maxSizeMB, refType, onChange]);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (disabled) return;
    const file = e.dataTransfer.files?.[0];
    doUpload(file);
  };

  const handleRemove = (e) => {
    e.stopPropagation();
    if (previewUrl) { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }
    onChange?.(null);
  };

  return (
    <div className="bks-field">
      {label && <label className="bks-label">{label}</label>}
      <div
        className={`bks-img-uploader ${dragging ? 'dragging' : ''} ${disabled ? 'disabled' : ''}`}
        style={{ aspectRatio: aspect }}
        onClick={() => !disabled && inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); if (!disabled) setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
      >
        {displayUrl ? (
          <>
            <img src={displayUrl} alt="preview" className="bks-img-preview" />
            {!disabled && (
              <button
                type="button"
                className="bks-img-remove"
                onClick={handleRemove}
                aria-label="حذف تصویر"
              >
                <TrashIcon size={14} />
              </button>
            )}
          </>
        ) : (
          <div className="bks-img-placeholder">
            {uploading
              ? <CircularProgress progress={uploadProgress} onCancel={() => abortControllerRef.current?.abort()} /> // 🆕
              : <><UploadIcon size={32} /><span>برای انتخاب کلیک کنید</span></>}
          </div>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          disabled={disabled}
          onChange={(e) => { doUpload(e.target.files?.[0]); e.target.value = ''; }}
        />
      </div>
      {hint && <div className="bks-hint">{hint}</div>}
      {error && <div className="bks-error">{error}</div>}
    </div>
  );
}

export function VideoUploader({
  videos = [], // آرایه‌ی VideoRef
  onAdd,
  onRemove,
  disabled = false,
  maxSizeMB = 200,
  hint = '',
}) {
  const { upload, uploading, progress, speed, cancel } = useVideoUpload();
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState(null);
  const [title, setTitle] = useState('');

  const handleFiles = useCallback(async (files) => {
    if (!files || files.length === 0) return;
    setError(null);
    for (const file of Array.from(files)) {
      if (!file.type.startsWith('video/')) {
        setError('فقط فایل‌های ویدیویی مجاز هستند.');
        continue;
      }
      if (file.size > maxSizeMB * 1024 * 1024) {
        setError(`حجم هر ویدیو نباید از ${maxSizeMB}MB بیشتر باشد.`);
        continue;
      }
      try {
        const res = await upload(file, { title: title || file.name });
        onAdd?.({
          blobId: res.blobId,
          title: title || file.name,
          mimeType: file.type,
          size: file.size,
        });
        setTitle('');
      } catch (e) {
        setError(e.message || 'خطا در آپلود');
      }
    }
  }, [upload, maxSizeMB, onAdd, title]);

  return (
    <div className="bks-video-uploader">
      <div
        className={`bks-drop-zone ${dragging ? 'dragging' : ''} ${disabled ? 'disabled' : ''}`}
        onDragOver={(e) => { e.preventDefault(); if (!disabled) setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          if (!disabled) handleFiles(e.dataTransfer.files);
        }}
        onClick={() => !disabled && !uploading && inputRef.current?.click()}
      >
        {uploading ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
            <CircularProgress progress={progress} speed={speed} onCancel={cancel} />
            <div className="bks-drop-title">در حال آپلود...</div>
          </div>
        ) : (
          <>
            <UploadIcon size={32} />
            <div className="bks-drop-title">فایل ویدیو را اینجا رها کنید</div>
            <div className="bks-drop-sub">یا برای انتخاب کلیک کنید</div>
          </>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="video/*"
          multiple
          style={{ display: 'none' }}
          disabled={disabled}
          onChange={(e) => { handleFiles(e.target.files); e.target.value = ''; }}
        />
      </div>

      {uploading && (
        <div className="bks-upload-progress">
          <ProgressBar value={progress} showLabel />
          <button type="button" className="bks-btn bks-btn-ghost" onClick={cancel}>
            لغو
          </button>
        </div>
      )}

      {!disabled && (
        <div className="bks-field" style={{ marginTop: 8 }}>
          <input
            type="text"
            className="bks-input"
            placeholder="عنوان ویدیو (اختیاری)"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>
      )}

      {hint && <div className="bks-hint">{hint}</div>}
      {error && <div className="bks-error">{error}</div>}

      {videos.length > 0 && (
        <div className="bks-video-list">
          {videos.map((v, i) => (
            <VideoItem
              key={v._id || i}
              video={v}
              index={i}
              onRemove={disabled ? null : () => onRemove?.(v)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function VideoItem({ video, index, onRemove }) {
  const { url } = useBlobUrl(video.blobId);
  const [playing, setPlaying] = useState(false);
  const videoRef = useRef(null);

  const togglePlay = () => {
    setPlaying((p) => !p);
  };

  useEffect(() => {
    if (!playing && videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  }, [playing]);

  return (
    <div className="bks-video-item">
      <div className="bks-video-item-thumb" onClick={togglePlay}>
        {playing && url ? (
          <video
            ref={videoRef}
            src={url}
            controls
            autoPlay
            className="bks-video-mini"
          />
        ) : (
          <>
            <div className="bks-video-thumb-overlay">
              <PlayIcon />
            </div>
            {video.title && <span className="bks-video-item-title">{video.title}</span>}
          </>
        )}
      </div>
      <div className="bks-video-item-info">
        <span className="bks-video-item-name">{video.title || `ویدیو ${index + 1}`}</span>
        {video.size > 0 && (
          <span className="bks-video-item-size">
            {(video.size / (1024 * 1024)).toFixed(1)} MB
          </span>
        )}
      </div>
      {onRemove && (
        <button
          type="button"
          className="bks-btn-icon-danger"
          onClick={onRemove}
          aria-label="حذف ویدیو"
        >
          <TrashIcon size={14} />
        </button>
      )}
    </div>
  );
}

export function VideoPreview({ blobId, title = '', externalUrl = null, className = '' }) {
  const { url, loading } = useBlobUrl(blobId);
  const src = url || externalUrl;

  if (loading) {
    return (
      <div className={`bks-video-preview bks-video-loading ${className}`}>
        <Spinner size={24} />
      </div>
    );
  }
  if (!src) {
    return (
      <div className={`bks-video-preview bks-video-empty ${className}`}>
        <VideoIcon size={28} />
        <span>ویدیویی موجود نیست</span>
      </div>
    );
  }
  return (
    <div className={`bks-video-preview ${className}`}>
      <video src={src} controls preload="metadata" />
      {title && <div className="bks-video-title">{title}</div>}
    </div>
  );
}

// ================================================================
// SECTION 8: DIFF VIEWER
// ================================================================

const DIFF_TONES = {
  added:    { bg: '#dcfce7', fg: '#166534', label: 'اضافه شد' },
  removed:  { bg: '#fee2e2', fg: '#991b1b', label: 'حذف شد' },
  modified: { bg: '#fef3c7', fg: '#92400e', label: 'تغییر کرد' },
  moved:    { bg: '#dbeafe', fg: '#1e40af', label: 'جابه‌جا شد' },
};

export function DiffViewer({ diff, className = '' }) {
  if (!diff || !diff.ok) {
    return <EmptyState icon="📊" title="تفاوتی برای نمایش وجود ندارد" />;
  }

  const sections = [
    { key: 'chapters', label: 'فصل‌ها', icon: '📚' },
    { key: 'lessons',  label: 'درس‌ها', icon: '📖' },
    { key: 'steps',    label: 'مراحل', icon: '🎯' },
    { key: 'videos',   label: 'ویدیوها', icon: '🎬' },
  ];

  const totalChanges =
    diff.summary.addedCount +
    diff.summary.removedCount +
    diff.summary.modifiedCount +
    diff.summary.movedCount;

  return (
    <div className={`bks-diff ${className}`}>
      <div className="bks-diff-summary">
        <DiffStat tone="added"    count={diff.summary.addedCount}    label="اضافه" />
        <DiffStat tone="removed"  count={diff.summary.removedCount}  label="حذف" />
        <DiffStat tone="modified" count={diff.summary.modifiedCount} label="تغییر" />
        <DiffStat tone="moved"    count={diff.summary.movedCount}    label="جابه‌جایی" />
      </div>

      {totalChanges === 0 ? (
        <EmptyState icon="🟰" title="هیچ تفاوتی وجود ندارد" />
      ) : (
        <div className="bks-diff-sections">
          {sections.map((sec) => (
            <DiffSection key={sec.key} section={sec} data={diff[sec.key]} />
          ))}
        </div>
      )}
    </div>
  );
}

function DiffStat({ tone, count, label }) {
  const t = DIFF_TONES[tone];
  return (
    <div className="bks-diff-stat" style={{ background: t.bg, color: t.fg }}>
      <span className="bks-diff-stat-num">{count}</span>
      <span className="bks-diff-stat-label">{label}</span>
    </div>
  );
}

function DiffSection({ section, data }) {
  if (!data) return null;
  const hasAny =
    data.added.length + data.removed.length + data.modified.length + data.moved.length;
  if (hasAny === 0) return null;

  return (
    <div className="bks-diff-section">
      <div className="bks-diff-section-title">
        <span>{section.icon}</span> {section.label}
      </div>
      <div className="bks-diff-section-body">
        <DiffGroup tone="added"    items={data.added}    />
        <DiffGroup tone="removed"  items={data.removed}  />
        <DiffGroup tone="modified" items={data.modified} />
        <DiffGroup tone="moved"    items={data.moved}    />
      </div>
    </div>
  );
}

function DiffGroup({ tone, items }) {
  if (!items || items.length === 0) return null;
  const t = DIFF_TONES[tone];
  return (
    <div className="bks-diff-group">
      <div className="bks-diff-group-head" style={{ color: t.fg, background: t.bg }}>
        <span className="bks-diff-group-badge">{t.label}</span>
        <span className="bks-diff-group-count">{items.length}</span>
      </div>
      <ul className="bks-diff-list">
        {items.map((it, i) => (
          <li key={it._id || i} className="bks-diff-item">
            <span className="bks-diff-item-title">{it.title || '(بدون عنوان)'}</span>
            {it.fieldsChanged && (
              <span className="bks-diff-item-fields">
                {it.fieldsChanged.map((f) => <span key={f} className="bks-diff-field">{f}</span>)}
              </span>
            )}
            {it.fromOrder != null && it.toOrder != null && (
              <span className="bks-diff-item-move">
                {it.fromOrder} → {it.toOrder}
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ================================================================
// SECTION 9: IMPACT PANEL
// ================================================================

export function ImpactPanel({ impact, className = '' }) {
  if (!impact) return null;
  if (impact.loading) {
    return (
      <div className={`bks-impact bks-impact-loading ${className}`}>
        <Spinner size={18} /> در حال تحلیل تأثیر...
      </div>
    );
  }
  if (!impact.data || !impact.data.ok) return null;

  const s = impact.data.summary;
  const hasBreaking = impact.data.hasBreakingChanges;

  return (
    <div className={`bks-impact ${hasBreaking ? 'bks-impact-warning' : ''} ${className}`}>
      <div className="bks-impact-head">
        <AlertIcon size={18} />
        <span>تحلیل تأثیر بر دانش‌آموزان</span>
      </div>
      <div className="bks-impact-grid">
        <div className="bks-impact-cell">
          <div className="bks-impact-num">{s.totalStudents}</div>
          <div className="bks-impact-label">دانش‌آموز فعال</div>
        </div>
        <div className={`bks-impact-cell ${s.studentsLosingProgress > 0 ? 'warn' : ''}`}>
          <div className="bks-impact-num">{s.studentsLosingProgress}</div>
          <div className="bks-impact-label">دانش‌آموز با داده آرشیوی</div>
        </div>
        <div className="bks-impact-cell">
          <div className="bks-impact-num">{s.removedStepCount}</div>
          <div className="bks-impact-label">مرحله حذف‌شده</div>
        </div>
        <div className="bks-impact-cell">
          <div className="bks-impact-num">{s.addedStepCount}</div>
          <div className="bks-impact-label">مرحله جدید</div>
        </div>
      </div>
      {hasBreaking && (
        <div className="bks-impact-note">
          ⚠️ حذف مراحل باعث آرشیو شدن داده‌ی پیشرفت می‌شود (پاک نمی‌شود).
        </div>
      )}
    </div>
  );
}

// ================================================================
// SECTION 10: VERSION BADGE
// ================================================================

const VERSION_TONES = {
  draft:       { tone: 'neutral', label: 'پیش‌نویس',       icon: '✏️' },
  pending:     { tone: 'warning', label: 'در انتظار بررسی', icon: '⏳' },
  published:   { tone: 'success', label: 'منتشرشده',        icon: '✓' },
  superseded:  { tone: 'neutral', label: 'جایگزین‌شده',     icon: '↩️' },
  rejected:    { tone: 'danger',  label: 'ردشده',          icon: '✕' },
  archived:    { tone: 'neutral', label: 'آرشیو',          icon: '📦' },
};

export function VersionBadge({ status, size = 'md' }) {
  const t = VERSION_TONES[status] || VERSION_TONES.draft;
  return (
    <Badge tone={t.tone} size={size}>
      <span style={{ marginInlineEnd: 4 }}>{t.icon}</span>
      {t.label}
    </Badge>
  );
}

const PROJECT_TONES = {
  building:         { tone: 'info',    label: 'در حال ساخت' },
  'pending-initial':{ tone: 'warning', label: 'در انتظار بررسی اولیه' },
  published:        { tone: 'success', label: 'منتشرشده' },
  'update-pending': { tone: 'warning', label: 'در انتظار بروزرسانی' },
  suspended:        { tone: 'danger',  label: 'معلق' },
};

export function ProjectStateBadge({ state, size = 'md' }) {
  const t = PROJECT_TONES[state] || PROJECT_TONES.building;
  return <Badge tone={t.tone} size={size}>{t.label}</Badge>;
}

// ================================================================
// SECTION 11: NOTIFICATION BELL
// ================================================================

export function NotificationBell({ onNavigate = null, className = '' }) {
  const { items, markRead, markAllRead, remove } = useNotifications({ limit: 50 });
  const unread = useUnreadCount();
  const [open, setOpen] = useState(false);

  const handleClick = (n) => {
    markRead(n.id);
    if (n.link && onNavigate) {
      onNavigate(n.link);
      setOpen(false); // بستن مودال بعد از کلیک روی لینک
    }
  };

  return (
    <div className={`bks-notif ${className}`}>
      <button
        type="button"
        className="bks-notif-btn"
        onClick={() => setOpen(true)}
        aria-label={`اعلان‌ها (${unread} خوانده‌نشده)`}
      >
        <BellIcon size={20} />
        {unread > 0 && (
          <span className="bks-notif-dot">{unread > 99 ? '99+' : unread}</span>
        )}
      </button>

      <Modal
        isOpen={open}
        onClose={() => setOpen(false)}
        title="اعلان‌ها"
        size="md"
        className="bks-notif-modal-glass"
      >
        <div className="bks-notif-modal-body">
          <div className="bks-notif-header" style={{ padding: '0 0 16px 0', borderBottom: '1px solid var(--bks-border-soft, #eee)', marginBottom: 16 }}>
            <span>{unread > 0 ? `${unread} اعلان خوانده‌نشده` : 'اعلان‌ها'}</span>
            {unread > 0 && (
              <button type="button" className="bks-link" onClick={markAllRead}>
                علامت‌گذاری همه به‌عنوان خوانده‌شده
              </button>
            )}
          </div>
          <div className="bks-notif-list" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
            {items.length === 0 ? (
              <EmptyState icon="🔔" title="اعلان جدیدی ندارید" size="sm" />
            ) : (
              items.map((n) => (
                <div
                  key={n.id}
                  className={`bks-notif-item ${!n.read ? 'unread' : ''}`}
                  onClick={() => handleClick(n)}
                  role="button"
                  tabIndex={0}
                >
                  <div className="bks-notif-item-body">
                    <div className="bks-notif-item-title">{n.title}</div>
                    {n.body && <div className="bks-notif-item-text">{n.body}</div>}
                    <div className="bks-notif-item-time">{formatRelativeTime(n.createdAt)}</div>
                  </div>
                  <button
                    type="button"
                    className="bks-notif-x"
                    onClick={(e) => { e.stopPropagation(); remove(n.id); }}
                    aria-label="حذف"
                  >
                    <CloseIcon size={12} />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
}

function formatRelativeTime(ts) {
  if (!ts) return '';
  const diff = Date.now() - ts;
  if (diff < 60000) return 'همین حالا';
  if (diff < 3600000) return `${Math.floor(diff / 60000)} دقیقه پیش`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)} ساعت پیش`;
  if (diff < 604800000) return `${Math.floor(diff / 86400000)} روز پیش`;
  return new Date(ts).toLocaleDateString('fa-IR');
}












// ================================================================
// SECTION 11.5: SUBSCRIPTION UI 🆕
// ================================================================

/**
 * کارت اشتراک — زیر خط میانی نمایش داده می‌شود
 */
export function SubscriptionCard({
  state = 'locked',   // locked | expired | renewal-soon | active
  price = 0,
  durationDays = 365,
  daysLeft = 0,
  currency = 'تومان',
  onSubscribe,
  onRenew,
  loading = false,
  features = null,
}) {
  const defaultFeatures = [
    'دسترسی به همه‌ی فصل‌ها و درس‌ها',
    'شرکت در آزمون‌های مرحله‌ای',
    'دسترسی به بروزرسانی‌های آینده',
    `${durationDays} روز اعتبار`,
  ];
  const feats = Array.isArray(features) ? features : defaultFeatures;

  // ─── حالت فعال ───
  if (state === 'active') {
    return (
      <div className="bks-subscription-card state-active" style={{
        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
        boxShadow: '0 20px 50px rgba(16, 185, 129, 0.3)',
      }}>
        <div className="bks-subscription-card-icon">✅</div>
        <h3 className="bks-subscription-card-title">اشتراک شما فعال است</h3>
        <p className="bks-subscription-card-sub">
          {daysLeft > 0 ? `${daysLeft} روز از اشتراک شما باقی مانده` : 'اشتراک شما فعال است'}
        </p>
      </div>
    );
  }

  // ─── حالت منقضی / نزدیک به انقضا ───
  const isExpired = state === 'expired';
  const isRenewalSoon = state === 'renewal-soon';

  return (
    <div className={`bks-subscription-card state-${state}`}>
      <div className="bks-subscription-card-icon">
        {isExpired ? '🔒' : isRenewalSoon ? '⏰' : '💎'}
      </div>
      <h3 className="bks-subscription-card-title">
        {isExpired
          ? 'اشتراک شما منقضی شده'
          : isRenewalSoon
            ? 'اشتراک شما به‌زودی تمام می‌شود'
            : 'دسترسی کامل به این کتاب'}
      </h3>
      <p className="bks-subscription-card-sub">
        {isExpired
          ? 'برای ادامه‌ی یادگیری، اشتراک خود را تمدید کنید'
          : isRenewalSoon
            ? `تنها ${daysLeft} روز باقی مانده`
            : 'با پرداخت یک‌باره، به همه‌ی محتوا دسترسی پیدا کنید'}
      </p>

      <div className="bks-subscription-price">
        <span className="bks-subscription-price-num">{price.toLocaleString('fa-IR')}</span>
        <span className="bks-subscription-price-unit">{currency} / سالانه</span>
      </div>

      <div className="bks-subscription-features">
        {feats.map((f, i) => (
          <div key={i} className="bks-subscription-feature">
            <span style={{ color: '#fff', fontWeight: 'bold' }}>✓</span>
            <span>{f}</span>
          </div>
        ))}
      </div>

      <button
        className="bks-subscription-btn"
        onClick={isExpired || isRenewalSoon ? onRenew : onSubscribe}
        disabled={loading}
      >
        {loading
          ? '⏳ در حال پردازش...'
          : isExpired
            ? '🔄 تمدید اشتراک'
            : isRenewalSoon
              ? '🔄 تمدید زودهنگام'
              : '🚀 فعال‌سازی اشتراک'}
      </button>
    </div>
  );
}

/**
 * تایمر گوشه — روزهای باقی‌مانده
 */
export function SubscriptionTimer({ daysLeft = 0, status = 'active', onRenew }) {
  if (status !== 'active' || daysLeft <= 0) return null;

  const tone = daysLeft <= 7 ? 'danger' : daysLeft <= 30 ? 'warning' : '';
  return (
    <div className={`bks-subscription-timer ${tone ? `tone-${tone}` : ''}`}>
      <span>⏳</span>
      <span>{daysLeft.toLocaleString('fa-IR')} روز باقی‌مانده</span>
      {onRenew && daysLeft <= 30 && (
        <button className="bks-subscription-timer-renew" onClick={onRenew}>
          تمدید
        </button>
      )}
    </div>
  );
}

/**
 * مودال خرید / تمدید اشتراک
 */
export function SubscriptionModal({
  isOpen,
  onClose,
  project,
  mode = 'purchase',   // purchase | renew
  loading = false,
  onConfirm,
}) {
  if (!isOpen) return null;

  const price = Number(project?.subscriptionPrice) || 0;
  const durationDays = Number(project?.subscriptionDurationDays) || 365;
  const isRenew = mode === 'renew';

  const handleConfirm = () => {
    onConfirm?.();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={loading ? undefined : onClose}
      title={isRenew ? 'تمدید اشتراک' : 'فعال‌سازی اشتراک'}
      size="sm"
      closeOnBackdrop={!loading}
      closeOnEsc={!loading}
      footer={
        <>
          <button
            type="button"
            className="bks-btn bks-btn-ghost"
            onClick={onClose}
            disabled={loading}
          >
            انصراف
          </button>
          <button
            type="button"
            className="bks-btn bks-btn-primary"
            onClick={handleConfirm}
            disabled={loading}
          >
            {loading ? <Spinner size={14} /> : null}
            {isRenew ? 'تایید و پرداخت' : 'پرداخت و فعال‌سازی'}
          </button>
        </>
      }
    >
      <div className="bks-sub-modal-body">
        <div className="bks-sub-modal-price-box">
          <div className="bks-sub-modal-price-num">
            {price.toLocaleString('fa-IR')}
          </div>
          <div className="bks-sub-modal-price-unit">
            تومان — {durationDays} روز
          </div>
        </div>

        <div className="bks-sub-modal-features">
          <div className="bks-sub-modal-feature">
            <span className="bks-sub-modal-feature-icon">✓</span>
            <span>دسترسی کامل به همه‌ی فصل‌ها و درس‌ها</span>
          </div>
          <div className="bks-sub-modal-feature">
            <span className="bks-sub-modal-feature-icon">✓</span>
            <span>شرکت در آزمون‌های مرحله‌ای</span>
          </div>
          <div className="bks-sub-modal-feature">
            <span className="bks-sub-modal-feature-icon">✓</span>
            <span>دسترسی به محتوای آینده</span>
          </div>
          <div className="bks-sub-modal-feature">
            <span className="bks-sub-modal-feature-icon">✓</span>
            <span>{durationDays} روز اعتبار از زمان خرید</span>
          </div>
        </div>

        <div style={{
          fontSize: 12,
          color: '#6b7280',
          textAlign: 'center',
          padding: '8px',
          background: '#f9fafb',
          borderRadius: 8,
        }}>
          {isRenew
            ? 'تمدید به انقضای فعلی اضافه می‌شود.'
            : 'با اولین پرداخت، اشتراک شما فعال می‌شود.'}
        </div>
      </div>
    </Modal>
  );
}








// ================================================================
// SECTION 12: EXPORTS
// ================================================================

export default {
  // Primitives
  Spinner, ProgressBar, Badge, Chip,
  // Layout
  EmptyState, SkeletonLoader, LockBanner, AutoSaveIndicator,
  // Modal
  Modal, ConfirmModal, Wizard,
  // Media
  BlobImage, ImageUploader, VideoUploader, VideoPreview,
  // Diff
  DiffViewer,
  // Impact
  ImpactPanel,
  // Badges
  VersionBadge, ProjectStateBadge,
  // Notification
  NotificationBell,

  SubscriptionCard,
  SubscriptionTimer,
  SubscriptionModal,
};